import React, { useEffect, useState } from "react";
import { Plus, Trash2, Sparkles, Lock, Check } from "lucide-react";
import * as Icons from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function Apps() {
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [installed, setInstalled] = useState([]);
  const [installer, setInstaller] = useState(null); // app being configured
  const [config, setConfig] = useState({});

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/apps"), api.get("/apps/installed")]);
    setCatalog(c.data.apps || []);
    setCategories(c.data.categories || ["All"]);
    setInstalled(i.data.installed || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = active === "All" ? catalog : catalog.filter((a) => a.category === active);

  const openInstaller = (app) => {
    setInstaller(app);
    const initial = {};
    (app.fields || []).forEach((f) => { initial[f.key] = f.default ?? ""; });
    setConfig(initial);
  };

  const installApp = async () => {
    if (!installer) return;
    try {
      await api.post("/apps/installed", { app_id: installer.id, name: installer.name, config });
      toast.success(`${installer.name} added`);
      setInstaller(null); setConfig({}); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uninstall = async (instance) => {
    if (!window.confirm(`Remove ${instance.name}?`)) return;
    try { await api.delete(`/apps/installed/${instance.id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Apps Marketplace</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
            Plug-in <span style={accentFont} className="text-sky-600">power</span>.
          </h1>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl">
            Add YouTube, Google Slides, Power BI, Instagram, RSS, Weather, and more — install once, drop into any screen.
          </p>
        </div>

        {/* Installed apps */}
        {installed.length > 0 && (
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.25em] text-sky-600 mb-3">Your installed apps</div>
            <div className="flex flex-wrap gap-3" data-testid="installed-apps">
              {installed.map((inst) => (
                <div key={inst.id} className="px-4 py-2 rounded-full bg-sky-50 border border-sky-200 text-sm flex items-center gap-3" data-testid={`installed-${inst.id}`}>
                  <Check className="h-4 w-4 text-sky-700" />
                  {inst.name}
                  <button onClick={() => uninstall(inst)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="apps-categories">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              data-testid={`cat-${cat}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                active === cat
                  ? "bg-sky-50 border-sky-300 text-sky-700"
                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Catalog grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="apps-grid">
          {filtered.map((app) => {
            const Icon = Icons[app.icon] || Sparkles;
            return (
              <div key={app.id} className="rounded-3xl border bg-white border-slate-200 backdrop-blur-xl p-5 hover:-translate-y-0.5 hover:border-sky-300 transition-all" data-testid={`app-${app.id}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${app.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <Icon className="h-7 w-7 text-slate-900" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold tracking-tight" style={headingFont}>{app.name}</h3>
                      {app.tier === "pro_plus" && <Lock className="h-3 w-3 text-amber-300" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">{app.category}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-500 mb-4 min-h-[2.5rem]">{app.description}</p>
                <Button
                  onClick={() => openInstaller(app)}
                  className="rounded-full bg-sky-500 hover:bg-sky-600 text-white text-xs w-full shadow-sm"
                  data-testid={`install-${app.id}`}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />Install
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Installer dialog */}
      <Dialog open={!!installer} onOpenChange={(o) => { if (!o) setInstaller(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle style={headingFont}>Install {installer?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{installer?.description}</p>
            {(installer?.fields || []).map((f) => (
              <div key={f.key}>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">{f.label}</label>
                {f.type === "boolean" ? (
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!config[f.key]}
                      onChange={(e) => setConfig({ ...config, [f.key]: e.target.checked })}
                      className="accent-sky-500"
                      data-testid={`installer-${f.key}`}
                    />
                    Enabled
                  </label>
                ) : f.type === "select" ? (
                  <select
                    value={config[f.key] || f.default || ""}
                    onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                    className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900 w-full"
                    data-testid={`installer-${f.key}`}
                  >
                    {f.options.map((o) => <option key={o} value={o} className="bg-white">{o}</option>)}
                  </select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    value={config[f.key] ?? ""}
                    onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                    placeholder={f.placeholder || ""}
                    className="bg-slate-50 border-slate-200 text-slate-900"
                    data-testid={`installer-${f.key}`}
                  />
                )}
              </div>
            ))}
            <Button onClick={installApp} className="w-full rounded-full bg-sky-500 hover:bg-sky-600" data-testid="installer-confirm">
              <Sparkles className="h-4 w-4 mr-1.5" /> Install
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
